import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
// Removed axios dependency - using fetch instead
import { 
  Building2, 
  Check, 
  X, 
  Eye, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar,
  Clock,
  CreditCard,
  Send,
  User,
  Plus
} from "lucide-react";
import AgencyDetailsModal from "./AgencyDetailsModal";

const API = process.env.REACT_APP_BACKEND_URL;

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { 'Authorization': `Bearer ${token}` } : {};
};

const AdminPendingAgencies = ({ onImpersonate = null, loadingImpersonation = false }) => {
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, approved, rejected, all
  const [selectedAgency, setSelectedAgency] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadAgencies();
  }, []);

  const loadAgencies = async () => {
    try {
      setLoading(true);
      console.log("Loading agencies from:", `${API}/api/v1/admin/agenzie`);
      
      const response = await fetch(`${API}/api/v1/admin/agenzie`, {
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log("Loaded agencies:", data.length);
      setAgencies(data);
    } catch (error) {
      console.error("Error loading agencies:", error);
      toast.error("Errore nel caricamento agenzie");
      // Set empty array to prevent infinite loading
      setAgencies([]);
    } finally {
      setLoading(false);
    }
  };

  const approveAgency = async (agencyId) => {
    try {
      const response = await fetch(`${API}/api/v1/admin/agenzie/${agencyId}/approve`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      toast.success(`✅ Agenzia approvata! Credenziali inviate a: ${data.username}`);
      
      // Update agency status locally
      setAgencies(agencies.map(agency => 
        agency.id === agencyId ? { ...agency, stato: 'approved', stato_approvazione: 'approved', username: data.username } : agency
      ));
    } catch (error) {
      console.error("Error approving agency:", error);
      toast.error("Errore nell'approvazione agenzia");
    }
  };

  const rejectAgency = async (agencyId) => {
    try {
      const response = await fetch(`${API}/api/v1/admin/agenzie/${agencyId}/stato?nuovo_stato=rejected`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      toast.success("Agenzia rifiutata");
      
      // Update agency status locally
      setAgencies(agencies.map(agency => 
        agency.id === agencyId ? { ...agency, stato: 'rejected', stato_approvazione: 'rejected' } : agency
      ));
    } catch (error) {
      console.error("Error rejecting agency:", error);
      toast.error("Errore nel rifiuto agenzia");
    }
  };

  const resendCredentials = async (agencyId) => {
    try {
      const response = await fetch(`${API}/api/v1/admin/agenzie/${agencyId}/send-credentials`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      toast.success("Credenziali reinviate con successo");
    } catch (error) {
      console.error("Error resending credentials:", error);
      toast.error("Errore nell'invio credenziali");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-700">In Attesa</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-700">Approvata</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700">Rifiutata</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-700">Sconosciuto</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString('it-IT', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return "N/A";
    }
  };

  const filteredAgencies = agencies.filter(agency => {
    const status = agency.stato_approvazione || agency.stato || 'pending';
    switch (filter) {
      case 'pending':
        return status === 'pending';
      case 'approved':
        return status === 'approved';
      case 'rejected':
        return status === 'rejected';
      default:
        return true;
    }
  });

  const pendingCount = agencies.filter(a => (a.stato_approvazione || 'pending') === 'pending').length;
  const approvedCount = agencies.filter(a => (a.stato_approvazione || 'pending') === 'approved').length;
  const rejectedCount = agencies.filter(a => (a.stato_approvazione || 'pending') === 'rejected').length;

  if (loading) {
    return (
      <div className="space-y-4">
        {[...Array(3)].map((_, index) => (
          <Card key={index} className="animate-pulse">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                <div className="flex-1">
                  <div className="h-5 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-500" />
              Gestione Agenzie
              {pendingCount > 0 && (
                <Badge className="bg-yellow-500 text-white">
                  {pendingCount} in attesa
                </Badge>
              )}
            </CardTitle>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate('/agenzia-onboarding')}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuova Agenzia
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={loadAgencies}
              >
                Aggiorna
              </Button>
            </div>
          </div>
        </CardHeader>
        
        {/* Filtri */}
        <CardContent className="border-t border-gray-100 pt-4">
          <div className="flex gap-2 flex-wrap items-center">
            <Button
              variant={filter === 'pending' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('pending')}
              className={filter === 'pending' ? 'bg-yellow-500' : ''}
            >
              In Attesa ({pendingCount})
            </Button>
            <Button
              variant={filter === 'approved' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('approved')}
              className={filter === 'approved' ? 'bg-green-500' : ''}
            >
              Approvate ({approvedCount})
            </Button>
            <Button
              variant={filter === 'rejected' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('rejected')}
              className={filter === 'rejected' ? 'bg-red-500' : ''}
            >
              Rifiutate ({rejectedCount})
            </Button>
            <Button
              variant={filter === 'all' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter('all')}
            >
              Tutte ({agencies.length})
            </Button>

            {/* Basic search bar: toggles visibility based on LIKE match */}
            <div className="ml-auto w-full sm:w-64">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cerca agenzia (nome/città)"
                className="w-full px-3 py-2 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Lista Agenzie */}
      <div className="space-y-4">
        {filteredAgencies.map((agency) => {
          const name = (agency.nome_agenzia || agency.nome || '').toLowerCase();
          const city = (agency.citta || '').toLowerCase();
          const query = searchTerm.trim().toLowerCase();
          const matchesSearch = !query || name.includes(query) || city.includes(query);
          return (
          <Card key={agency.id} className="hover:shadow-md transition-shadow" style={{ display: matchesSearch ? 'block' : 'none' }}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                {/* Logo/Icon */}
                <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
                  {agency.nome_agenzia?.charAt(0) || 'A'}
                </div>
                
                {/* Informazioni Agenzia */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {agency.nome_agenzia || agency.nome}
                      </h3>
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {agency.citta || 'N/A'} {agency.provincia ? `(${agency.provincia})` : ''}
                        </span>
                        {agency.anni_esperienza && (
                          <span className="flex items-center gap-1">
                            <Building2 className="w-3 h-3" />
                            {agency.anni_esperienza} anni esperienza
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      {getStatusBadge(agency.stato_approvazione || agency.stato || 'pending')}
                      <span className="text-xs text-gray-500">
                        {formatDate(agency.data_registrazione || agency.created_at)}
                      </span>
                    </div>
                  </div>

                  {/* Contatti */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Contatti:</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2">
                        <Mail className="w-3 h-3 text-gray-400" />
                        <span>{agency.email || 'N/A'}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Phone className="w-3 h-3 text-gray-400" />
                        <span>{agency.telefono || 'N/A'}</span>
                      </div>
                      {agency.partita_iva && (
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-3 h-3 text-gray-400" />
                          <span>P.IVA: {agency.partita_iva}</span>
                        </div>
                      )}
                      {agency.sito_web && (
                        <div className="flex items-center gap-2">
                          <Eye className="w-3 h-3 text-gray-400" />
                          <span>{agency.sito_web}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Azioni */}
                  <div className="flex gap-2 flex-wrap">
                    {(agency.stato_approvazione || 'pending') === 'pending' && (
                      <>
                        <Button 
                          size="sm" 
                          className="bg-green-500 hover:bg-green-600 text-white"
                          onClick={() => approveAgency(agency.id)}
                        >
                          <Check className="w-3 h-3 mr-1" />
                          Approva
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => rejectAgency(agency.id)}
                        >
                          <X className="w-3 h-3 mr-1" />
                          Rifiuta
                        </Button>
                      </>
                    )}
                    
                    {(agency.stato_approvazione || 'pending') === 'approved' && (
                      <>
                        {agency.username && (
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => resendCredentials(agency.id)}
                          >
                            <Send className="w-3 h-3 mr-1" />
                            Reinvia Credenziali
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onImpersonate && onImpersonate('agenzia', agency.id, agency.nome_agenzia)}
                          disabled={loadingImpersonation}
                          className="border-blue-300 text-blue-600 hover:bg-blue-50"
                        >
                          <Eye className="w-3 h-3 mr-1" />
                          Entra nella Dashboard
                        </Button>
                      </>
                    )}
                    
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        setSelectedAgency(agency);
                        setShowDetailsModal(true);
                      }}
                    >
                      <Eye className="w-3 h-3 mr-1" />
                      Dettagli
                    </Button>
                  </div>

                  {/* Note aggiuntive */}
                  {agency.note && (
                    <div className="mt-3 p-2 bg-blue-50 rounded text-sm text-blue-700">
                      <strong>Note:</strong> {agency.note}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );})}
      </div>

      {filteredAgencies.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'pending' ? 'Nessuna agenzia in attesa' : `Nessuna agenzia ${filter}`}
            </h3>
            <p className="text-gray-500">
              {filter === 'pending' 
                ? 'Le nuove registrazioni agenzie appariranno qui per l\'approvazione'
                : 'Le agenzie filtrate appariranno qui'
              }
            </p>
          </CardContent>
        </Card>
      )}

      {/* Agency Details Modal */}
      {showDetailsModal && (
        <AgencyDetailsModal
          agency={selectedAgency}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedAgency(null);
          }}
          onApprove={(agencyId, data) => {
            // Update agency status locally
            setAgencies(agencies.map(agency => 
              agency.id === agencyId 
                ? { ...agency, stato_approvazione: 'approved', username: data.username }
                : agency
            ));
          }}
          onReject={(agencyId) => {
            // Update agency status locally
            setAgencies(agencies.map(agency => 
              agency.id === agencyId 
                ? { ...agency, stato_approvazione: 'rejected' }
                : agency
            ));
          }}
        />
      )}
    </div>
  );
};

export default AdminPendingAgencies;